import { useState, useCallback, useRef } from 'react';
import { employeeApi } from '../../../../api/employeeApi';
import { leaderApi } from '../../../../api/leaderApi';

/**
 * Hook to manage employee data fetching and state.
 */
export function useEmployeeData() {
    const [employees, setEmployees] = useState([]);
    const [categories, setCategories] = useState([]);
    const [stats, setStats] = useState({ total: 0, available: 0, onLeave: 0, sick: 0, absent: 0 });
    const [absences, setAbsences] = useState({}); // worker_id -> []
    const [assignments, setAssignments] = useState([]);
    const [documentTypes, setDocumentTypes] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [kolonnen, setKolonnen] = useState([]);
    const [loading, setLoading] = useState(true);

    const initializedRef = useRef({ certWorker: false, certType: false });
    const lastCountsRef = useRef({ employees: 0, categories: 0, documentTypes: 0 });

    const loadAll = useCallback(async () => {
        try {
            setLoading(true);
            const [empData, catData, statsData, assignmentsData, typeData, docData, vehiclesData, kolonnenData] = await Promise.all([
                employeeApi.getEmployees(),
                employeeApi.getCategories(),
                employeeApi.getStats(),
                leaderApi.getAssignments(),
                employeeApi.getDocumentTypes(),
                employeeApi.getDocuments(),
                leaderApi.getVehicles(),
                leaderApi.getKolonnen()
            ]);

            setEmployees(empData);
            setCategories(catData);
            setStats(statsData);
            setAssignments(assignmentsData);
            setDocumentTypes(typeData);
            setDocuments(docData);
            setVehicles(vehiclesData);
            setKolonnen(kolonnenData);

            // Load absences for all employees
            const today = new Date().toISOString().split('T')[0];
            const absMap = {};
            await Promise.all(
                empData.map(async (w) => {
                    try {
                        const abs = await employeeApi.getWorkerAbsences(w.id, { start: today });
                        absMap[w.id] = abs;
                    } catch { absMap[w.id] = []; }
                })
            );

            setAbsences(absMap);
            
            // Update counts for sync logic (used by filter hooks)
            lastCountsRef.current.employees = empData.length;
            lastCountsRef.current.categories = catData.length;
            lastCountsRef.current.documentTypes = typeData.length;

        } catch (err) {
            console.error('Error loading employees:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    return {
        employees,
        categories,
        stats,
        absences,
        assignments,
        documentTypes,
        documents,
        vehicles,
        kolonnen,
        loading,
        loadAll,
        initializedRef,
        lastCountsRef
    };
}
